<template>
    <v-dialog width="700" v-model="viewCheckListDialog" :scrollable="true">
        <v-card class="checklist-card">
            <v-card-title class="odometer-title font-weight-bold white--text pa-5">
                <v-icon class="mr-3" color="white">mdi-clipboard-check-outline</v-icon>
                <span>Checklist History</span>

                <v-spacer></v-spacer>

                <v-btn icon @click="viewCheckListDialog = false" color="white">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-0">
                <!-- Tabs for Asset and Defect Checklists -->
                <v-tabs v-model="activeTab" background-color="transparent" color="primary" class="px-4"
                    @change="onTabChange">
                    <v-tab>
                        <v-icon small left>mdi-clipboard-check</v-icon>
                        Asset Checklist
                        <v-chip x-small class="ml-2" :color="assetChecklistData.length ? 'primary' : 'grey'"
                            text-color="white">
                            {{ assetChecklistData.length }}
                        </v-chip>
                    </v-tab>
                    <v-tab>
                        <v-icon small left>mdi-alert-circle</v-icon>
                        Defect Checklist
                        <v-chip x-small class="ml-2" :color="defectChecklistData.length ? 'error' : 'grey'"
                            text-color="white">
                            {{ defectChecklistData.length }}
                        </v-chip>
                    </v-tab>
                </v-tabs>

                <v-divider></v-divider>

                <!-- Tab Items -->
                <v-tabs-items v-model="activeTab">
                    <!-- Asset Checklist Tab -->
                    <v-tab-item>
                        <v-data-table :headers="assetChecklistHeaders" :items="assetChecklistData"
                            class="elevation-0 checklist-table" hide-default-footer :loading="loading"
                            loading-text="Loading checklist data...">
                            <!-- Date Column -->
                            <template v-slot:item.date="{ item }">
                                <div class="d-flex align-center py-2">
                                    <v-icon small class="mr-2" color="grey darken-1">mdi-calendar</v-icon>
                                    <span class="font-weight-medium">{{ item.date }}</span>
                                </div>
                            </template>

                            <!-- Submitted Status -->
                            <template v-slot:item.is_submitted="{ item }">
                                <v-chip small :color="item.is_submitted ? 'success' : 'warning'"
                                    :text-color="item.is_submitted ? 'white' : 'black'" class="font-weight-medium">
                                    <v-icon small left>
                                        {{ item.is_submitted ? 'mdi-check-circle' : 'mdi-clock-alert' }}
                                    </v-icon>
                                    {{ item.is_submitted ? 'Submitted' : 'Pending' }}
                                </v-chip>
                            </template>

                            <!-- Pass Status -->
                            <template v-slot:item.is_passed="{ item }">
                                <v-chip small :color="item.is_passed ? 'success' : 'error'" text-color="white"
                                    class="font-weight-medium">
                                    <v-icon small left>
                                        {{ item.is_passed ? 'mdi-check-circle' : 'mdi-close-circle' }}
                                    </v-icon>
                                    {{ item.is_passed ? 'Passed' : 'Failed' }}
                                </v-chip>
                            </template>

                            <!-- Actions -->
                            <template v-slot:item.actions="{ item, index }">
                                <div class="d-flex justify-center">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small color="primary" @click="viewAssetChecklist(item, index)"
                                                v-bind="attrs" v-on="on">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>View Details</span>
                                    </v-tooltip>

                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small color="info" @click="getTripDefectBlob()"
                                                v-bind="attrs" v-on="on">
                                                <v-icon small>mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Download CheckList</span>
                                    </v-tooltip>
                                </div>
                            </template>

                            <!-- No Data -->
                            <template v-slot:no-data>
                                <div class="text-center py-8">
                                    <v-icon size="64" color="grey lighten-1">mdi-clipboard-off-outline</v-icon>
                                    <div class="text-h6 grey--text text--darken-1 mt-3">No Asset Checklist Data</div>
                                    <div class="text-caption grey--text">No asset checklist records found for this trip
                                    </div>
                                </div>
                            </template>
                        </v-data-table>
                    </v-tab-item>

                    <!-- Defect Checklist Tab -->
                    <v-tab-item>
                        <v-data-table :headers="defectChecklistHeaders" :items="defectChecklistData"
                            class="elevation-0 checklist-table" hide-default-footer :loading="loadingDefects"
                            loading-text="Loading defect data...">
                            <!-- Date Column -->
                            <template v-slot:item.date="{ item }">
                                <div class="d-flex align-center py-2">
                                    <v-icon small class="mr-2" color="grey darken-1">mdi-calendar</v-icon>
                                    <span class="font-weight-medium">{{ item.date }}</span>
                                </div>
                            </template>

                            <!-- Asset Checklist -->
                            <template v-slot:item.asset_checklist="{ item }">
                                <v-chip small color="primary" text-color="white" class="font-weight-medium">
                                    <v-icon small left>mdi-clipboard-text</v-icon>
                                    {{ item.asset_checklist || 'N/A' }}
                                </v-chip>
                            </template>

                            <!-- Actions -->
                            <template v-slot:item.actions="{ item, index }">
                                <div class="d-flex justify-center">
                                    <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small color="primary" @click="viewDefectChecklist(item, index)"
                                                v-bind="attrs" v-on="on">
                                                <v-icon small>mdi-eye</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>View Details</span>
                                    </v-tooltip>

                                    <!-- <v-tooltip bottom>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn icon small color="info" @click="getTripDefectBlob()"
                                                v-bind="attrs" v-on="on" 
                                                :disabled="!item.defect_asset_checklist_items || item.defect_asset_checklist_items.length === 0">
                                                <v-icon small>mdi-download</v-icon>
                                            </v-btn>
                                        </template>
                                        <span>Download Defects </span>
                                    </v-tooltip> -->
                                </div>
                            </template>

                            <!-- No Data -->
                            <template v-slot:no-data>
                                <div class="text-center py-8">
                                    <v-icon size="64" color="grey lighten-1">mdi-alert-circle-outline</v-icon>
                                    <div class="text-h6 grey--text text--darken-1 mt-3">No Defect Checklist Data</div>
                                    <div class="text-caption grey--text">No defect checklist records found for this trip
                                    </div>
                                </div>
                            </template>
                        </v-data-table>
                    </v-tab-item>
                </v-tabs-items>
            </v-card-text>

            <!-- Footer with summary -->
            <v-divider></v-divider>
            <v-card-actions class="pa-4 grey lighten-4">
                <v-icon small class="mr-2" color="grey darken-1">mdi-format-list-bulleted</v-icon>
                <span class="text-caption grey--text text--darken-2">
                    Total Records: <strong>{{ getCurrentData.length }}</strong>
                </span>
                <v-spacer></v-spacer>
                <v-btn text small color="primary" @click="viewCheckListDialog = false">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>

        <AssetCheckListDetailsViewForm v-model="showAssetcheckListForm" :checkListItems="ChecklistDataItems" />

        <DefectCheckListDetailsViewForm v-model="showDefectCheckListForm" :defectItems="DefectDataItems" />
    </v-dialog>
</template>

<script>
import InputField from "@/components/FormBaseComponents/InputField.vue";
import MultiSelect from "@/components/FormBaseComponents/MultiSelect.vue";
import { AgGridVue } from "ag-grid-vue";
import AssetCheckListDetailsViewForm from "../dialogForms/AssetCheckListDetailsViewForm.vue";
import DefectCheckListDetailsViewForm from "../dialogForms/DefectCheckListDetailsViewForm.vue";
import * as XLSX from "xlsx";

export default {
    components: {
        AgGridVue,
        InputField,
        AssetCheckListDetailsViewForm,
        DefectCheckListDetailsViewForm,
        MultiSelect,
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        tripDetails: {
            type: Object,
            default: () => ({}),
        },
    },
    watch: {
        value(val) {
            if (val) {
                this.getAssetChecklistData();
                this.getDefectChecklistData();
            }
        },
    },
    computed: {
        viewCheckListDialog: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            },
        },
        context() {
            return { parentComponent: this };
        },
        getCurrentData() {
            return this.activeTab === 0 ? this.assetChecklistData : this.defectChecklistData;
        },
    },
    data() {
        return {
            formErrors: {},
            formDetails: {},
            ChecklistDataItems: [],
            DefectDataItems: [],
            showAssetcheckListForm: false,
            showDefectCheckListForm: false,
            isValid: false,
            loading: false,
            loadingDefects: false,
            downloadLoading: false,
            activeTab: 0,
            assetChecklistData: [],
            defectChecklistData: [],
            assetChecklistHeaders: [
                {
                    text: "Date",
                    align: "start",
                    sortable: false,
                    value: "date",
                    width: "30%",
                },
                {
                    text: "Submission Status",
                    value: "is_submitted",
                    sortable: false,
                    align: "center",
                    width: "25%",
                },
                {
                    text: "Pass Status",
                    value: "is_passed",
                    sortable: false,
                    align: "center",
                    width: "25%",
                },
                {
                    text: "Actions",
                    value: "actions",
                    align: "center",
                    sortable: false,
                    width: "20%",
                },
            ],
            defectChecklistHeaders: [
                {
                    text: "Date",
                    align: "start",
                    sortable: false,
                    value: "date",
                    width: "40%",
                },
               
                {
                    text: "Actions",
                    value: "actions",
                    align: "center",
                    sortable: false,
                    width: "25%",
                },
            ],
        };
    },
    methods: {
        onTabChange(tab) {

            if (tab === 1 && this.defectChecklistData.length === 0) {
                this.getDefectChecklistData();
            }
        },

        viewAssetChecklist(item, index) {
            this.ChecklistDataItems = item.items;
            this.showAssetcheckListForm = true;
        },

        viewDefectChecklist(item, index) {
           
            this.DefectDataItems = item.defect_asset_checklist_items ||  [];
            this.showDefectCheckListForm = true;

        },

        getAssetChecklistData(params = {}) {
            this.loading = true;
            params = {
                limit: "all",
                vehicle: this.tripDetails.vehicle,
                trailer: this.tripDetails.trailer,
                date: this.tripDetails.trip_start_date,
                ...params,
            };
            this.$api.vehicle
                .getAssetChecklist(params)
                .then((res) => {
                    this.assetChecklistData = res.data.map(item => ({
                        ...item,
                        downloading: false
                    }));
                })
                .catch((err) => {
                    console.error(err);
                    this.$toast.error("Failed to load asset checklist data");
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        getDefectChecklistData(params = {}) {
            this.loadingDefects = true;
            params = {
                limit: "all",
                vehicle: this.tripDetails.vehicle,
                trailer: this.tripDetails.trailer,
                date: this.tripDetails.trip_start_date,
                ...params,
            };

            this.$api.vehicle
                .getDefectAssetChecklist(params)
                .then((res) => {
                    this.defectChecklistData = res.data.map(item => ({
                        ...item
                    }));
                })
                .catch((err) => {
                    console.error(err);
                    this.$toast.error("Failed to load defect checklist data");
                })
                .finally(() => {
                    this.loadingDefects = false;
                });
        },

        getDefectStatusColor(status) {
            const statusColors = {
                'Open': 'error',
                'In Progress': 'warning',
                'Resolved': 'success',
                'Closed': 'grey',
            };
            return statusColors[status] || 'grey';
        },

        getTripDefectBlob() {
      this.$bus.$emit("showLoader", true);

      this.$api.trip
        .getTripDefectsBlob(this.tripDetails.id)
        .then(async (resp) => {
          let blob = new Blob(["\ufeff", resp.data], {
            type: "application/octet-stream",
          });
          let link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = this.tripDetails.trip_id + "_trip_checklist" + ".pdf";
          link.click();
          this.$bus.$emit("showLoader", false);
        })
        .catch((err) => {
          console.error(err);
          this.$bus.$emit("showLoader", false);
        });
    },

      
    },
};
</script>

<style scoped>
.checklist-card {
    border-radius: 8px;
    overflow: hidden;
}

.odometer-title {
    background: linear-gradient(135deg, #e6a205 0%, #d96512 100%);
}

.checklist-table>>>.v-data-table__wrapper {
    max-height: 450px;
    overflow-y: auto;
}

.checklist-table>>>tbody tr:hover {
    background-color: rgba(102, 126, 234, 0.05) !important;
}

.checklist-table>>>th {
    font-weight: 600 !important;
    color: rgba(0, 0, 0, 0.87) !important;
    background-color: #f5f5f5 !important;
}

.checklist-table>>>td {
    padding: 12px 16px !important;
}

.v-tab {
    text-transform: none !important;
    font-weight: 500;
}
</style>